/**
 * Factory for WebSked stories
 * @author Brendan Magee, Umer Hassan
 */

function StoryFactory(
  $http,
  $q,
  $log,
  $window,
  $sce,
  $translate,
  platformService,
  companyService,
  Pitch,
  analyticsService,
  distributorService,
  siteService,
  timezoneService,
  moment
) {
  function Story(storyData, config = {}) {
    if (!(this instanceof Story)) {
      return new Story(storyData, config);
    }
    const self = this;

    // Process required and optional arguments
    angular.extend(self, storyData);

    // Set `sourceType` and `source` if the story has a `distributorId`
    if (this.distributorId && distributorService.distributors) {
      const dist = distributorService.distributors.get(this.distributorId);
      this.sourceType = dist && dist.category;
      this.source = dist && dist.name;
    }

    // Set the display name for the website of the primary section
    if (this.primarySection && this.primarySection.website && siteService.cache.websites) {
      const matchingSite = siteService.cache.websites.find(site =>
        site._id === this.primarySection.website
      );
      this.primarySection.websiteName = matchingSite ? matchingSite.display_name : null;
    }

    // Set `creators` to `null` if none are listed
    if (self.creators === undefined || self.creators === null || self.creators.length === 0) {
      self.creators = null;
    }

    self.storyLengthUnit = companyService.cache && companyService.cache.storyLengthUnit
      ? companyService.cache.storyLengthUnit
      : 'inches';

    // Give the story a `websked` property if it does not have one already
    if (!self.websked) {
      self.websked = {};
    }

    // Process the headline and body as trusted HTML
    if (self.headline !== null && typeof self.headline === 'string') {
      self.websked.headline_clean = $sce.trustAsHtml(self.headline);
    }
    self.websked.html_clean = $sce.trustAsHtml(self.html);

    // If an edit URL is not present, fall back to Ellipsis
    if (!self.cmsEditURL) {
      // WebSked can run at a different routes, such as /websked/ and /websked-beta/
      const fullHost = $window.location.href.slice(0, $window.location.href.indexOf('/websked'));
      self.cmsEditURL = `${fullHost}/composer/#!/edit/${self.id}`;
    }

    // Get the plan name for each pitch
    if (self.pitches && self.pitches.length > 0) {
      angular.forEach(self.pitches, (currentPitch) => {
        const pitch = currentPitch;
        pitch.planName = platformService.getPlatformName(pitch.planPath);
      });
    }


    if (self.pitches) {
      self.pitches = self.pitches
        .map((pitch) => {
          const obj = pitch;
          obj.storyId = storyData.id;
          return new Pitch('platform', obj);
        });
    } else {
      self.pitches = [];
    }

    if (self.publicationPitches) {
      self.publicationPitches = self.publicationPitches
        .map((pubPitch) => {
          const obj = pubPitch;
          obj.storyId = storyData.id;
          return new Pitch('publication', obj);
        });
    } else {
      self.publicationPitches = [];
    }

    if (!self.pitches) {
      self.pitches = [];
    }

    // Find the relevant pitches for the publication or platform
    if (config.publication && self.publicationPitches && self.publicationPitches.length > 0) {
      angular.forEach(self.publicationPitches, (pitch) => {
        if (config.publication === pitch.publicationId) {
          self.websked.pitch = pitch;
        }
      });
    } else if (config.platform && self.pitches && self.pitches.length > 0) {
      angular.forEach(self.pitches, (pitch) => {
        if (config.platform === pitch.planPath) {
          self.websked.pitch = pitch;
        }
      });
    }

    // Setting translated content type
    const contentTypesDisplayMap = {
      article: $translate.instant('ARTICLE'),
      video: $translate.instant('VIDEO'),
      gallery: $translate.instant('GALLERY'),
      graphic: $translate.instant('GRAPHIC')
    };

    self.contentTypeDisplay = contentTypesDisplayMap[self.contentType] || self.contentType;
  }

  Story.get = (id, config) => {
    const deferred = $q.defer();

    if (!id) {
      throw new Error($translate.instant('PLEASE_PROVIDE_STORY_ID'));
    }

    $http.get(`api/stories?id=${id}`)
      .then((response) => {
        deferred.resolve(new Story(response.data, config));
      }, (e) => {
        deferred.reject(e);
      });

    return deferred.promise;
  };

  Story.prototype.killStory = function killStory() {
    const deferred = $q.defer();
    const instance = this;

    $http.post(`api/stories/kill?id=${instance.id}`)
      .then((response) => {
        $log.log(response.data);

        instance.kill = {
          isKilled: true
        };

        analyticsService.trackEvent('Story action', 'Story killed');

        deferred.resolve(instance);
      }, (error) => {
        $log.error('Kill story error: ', error);

        deferred.reject(error);
      });

    return deferred.promise;
  };

  Story.prototype.updateEta = function updateEta(newEta) {
    const deferred = $q.defer();
    const instance = this;

    if (!newEta) {
      throw new Error('This method requires a `newEta` argument');
    }

    const update = {
      dates: {
        planned: newEta
      }
    };

    $http.put(`api/stories/${instance.id}`, update)
      .then((response) => {
        $log.log(response.data);

        instance.dates.planned = response.data.dates.planned;

        analyticsService.trackEvent('Story action', instance.dates.websked ? 'Story ETA updated' : 'Updated time to Web');

        deferred.resolve(instance);
      }, (error) => {
        $log.error('Update ETA error: ', error);

        deferred.reject(error);
      });
    return deferred.promise;
  };

  Story.prototype.getFormattedDates = (story) => {
    const formattedDates = {};
    const dateFieldsToFormat = ['created', 'planned', 'published', 'updated', 'websked'];
    dateFieldsToFormat.forEach((field) => {
      if (story.dates[field]) {
        formattedDates[field] = moment(story.dates[field] * 1000)
          .tz(timezoneService.userTimezone)
          .format(timezoneService.userTimeHourFormat === 12 ? 'll h:mm:ss a' : 'll H:mm:ss');
      }
    });
    return formattedDates;
  };

  return Story;
}

const arr = [
  '$http',
  '$q',
  '$log',
  '$window',
  '$sce',
  '$translate',
  'platformService',
  'companyService',
  'Pitch',
  'analyticsService',
  'distributorService',
  'siteService',
  'timezoneService',
  'moment',
  StoryFactory
];

export default function (ngModule) {
  ngModule.factory('Story', arr);
}
